import type { VariantProps } from "class-variance-authority";
import { cva } from "class-variance-authority";
import { forwardRef, useEffect, useState } from "react";
import { cn } from "../../../lib";

const containerVariants = cva("h-full grid grid-cols-1", {
  variants: {
    size: {
      large: "w-12",
      medium: "w-10",
      small: "w-6",
    },
    selectedTintColor: {
      none: "",
      yellow: "bg-lf-yellow-light",
    },
  },
  defaultVariants: {
    size: "medium",
  },
});

const selectedIndicatorVariants = cva(
  "[grid-area:1/1] z-1 inset-y-0 left-0 h-full w-1",
  {
    variants: {
      selectedTintColor: {
        none: "",
        yellow: "bg-lf-yellow",
      },
    },
  },
);
const checkboxVariants = cva(
  "[grid-area:1/1] m-auto rounded border-gray-300 text-lf-blue focus:ring-lf-blue",
  {
    variants: {
      size: {
        large: "size-6",
        medium: "size-5",
        small: "size-4",
      },
    },
    defaultVariants: {
      size: "medium",
    },
  },
);

const activityIndicatorVariants = cva("[grid-area:1/1] m-auto rounded flex", {
  variants: {
    size: {
      large: "size-6",
      medium: "size-5",
      small: "size-4",
    },
  },
  defaultVariants: {
    size: "medium",
  },
});

interface AsyncCheckboxProps
  extends React.HTMLAttributes<HTMLInputElement>,
    VariantProps<typeof containerVariants>,
    VariantProps<typeof checkboxVariants>,
    VariantProps<typeof activityIndicatorVariants> {
  testId?: string;
  label?: string;
  isLoading?: boolean;
  initialState: boolean;
  callback?: (selected: boolean) => void;
  asyncCallback?: (selected: boolean, completion: () => void) => void;
}

/**
 * Alternate checkbox implementation - uses Tailwind Styling
 *
 * If asyncCallback is provided, the checkbox flips locally and shows a
 * spinner until `completion()` runs so overlapping toggles cannot fire.
 * `initialState` is the source of truth again when the parent confirms or
 * rolls the value back.
 */
const AsyncCheckbox = forwardRef<HTMLInputElement, AsyncCheckboxProps>(
  (props: AsyncCheckboxProps, ref: React.Ref<HTMLInputElement>) => {
    const {
      testId,
      size = "medium",
      selectedTintColor = "none",
      className,
      label,
      initialState,
      callback,
      asyncCallback,
    } = props;

    const [checked, setChecked] = useState(initialState);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
      setChecked(initialState);
    }, [initialState]);

    return (
      <div
        className={cn(
          containerVariants({ size, selectedTintColor }),
          className,
        )}
        ref={ref}
        data-testid={
          testId
            ? `${testId}-cb-container${checked ? "-flagged" : ""}`
            : ""
        }
      >
        {checked && (
          <div
            className={cn(selectedIndicatorVariants({ selectedTintColor }))}
            data-testid={testId ? `${testId}-cb-selected-indicator` : ""}
          />
        )}
        <input
          type="checkbox"
          className={cn(
            checkboxVariants({ size }),
            // Never `display:none` the input while loading: removing the
            // focused element from layout makes the browser drop focus and
            // re-anchor scroll, nudging the page up a few pixels (PAD-420).
            // Keep it in the grid cell, just invisible and inert.
            isLoading ? "opacity-0 pointer-events-none" : "inline",
          )}
          tabIndex={isLoading ? -1 : undefined}
          aria-busy={isLoading}
          value={label}
          checked={checked}
          onChange={(e) => {
            if (isLoading) return;
            const selected = e.target.checked;
            setChecked(selected);
            callback?.(selected);
            if (asyncCallback) {
              setIsLoading(true);
              asyncCallback(selected, () => setIsLoading(false));
            }
          }}
          data-testid={testId ? `${testId}-checkbox` : ""}
        />
        <div
          className={cn(
            activityIndicatorVariants({ size }),
            isLoading ? "flex" : "hidden",
          )}
        >
          <span className="flex-auto border-2 border-gray-300 border-b-lf-blue-dark rounded-full animate-spin"></span>
        </div>
      </div>
    );
  },
);

AsyncCheckbox.displayName = "Tailwind Checkbox";

export default AsyncCheckbox;
export type { AsyncCheckboxProps };
